import { BenchmarkMetric } from "./BenchmarkMetric";

const sections = [
  [
    "Standard test",
    "2,038/2,042 (99.80%)",
    "1,268/2,020 (62.77%)",
    "2,032/2,042 (99.51%)",
    "1,213/2,020 (60.05%)",
  ],
  [
    "Enron email",
    "1,845/1,847 (99.89%)",
    "1,377/2,308 (59.66%)",
    "1,798/1,847 (97.35%)",
    "1,240/2,308 (53.73%)",
  ],
  [
    "Llama",
    "2,035/2,035 (100.00%)",
    "1,255/1,878 (66.83%)",
    "2,035/2,035 (100.00%)",
    "1,194/1,878 (63.58%)",
  ],
];
const sources = [
  [
    "amazon_reviews",
    "544/546 (99.63%)",
    "303/549 (55.19%)",
    "541/546 (99.08%)",
    "284/549 (51.73%)",
  ],
  [
    "enron_email",
    "1,845/1,847 (99.89%)",
    "1,377/2,308 (59.66%)",
    "1,798/1,847 (97.35%)",
    "1,240/2,308 (53.73%)",
  ],
  [
    "fineweb_edu",
    "986/986 (100.00%)",
    "626/973 (64.34%)",
    "986/986 (100.00%)",
    "598/973 (61.46%)",
  ],
  [
    "google_reviews",
    "419/420 (99.76%)",
    "259/389 (66.58%)",
    "417/420 (99.29%)",
    "247/389 (63.50%)",
  ],
  [
    "news",
    "1,009/1,010 (99.90%)",
    "640/977 (65.51%)",
    "1,008/1,010 (99.80%)",
    "605/977 (61.92%)",
  ],
  [
    "reddit_writing_prompts",
    "1,115/1,115 (100.00%)",
    "695/1,010 (68.81%)",
    "1,115/1,115 (100.00%)",
    "673/1,010 (66.63%)",
  ],
];
const producers = [
  [
    "claude-sonnet-4-20250514",
    "1,374/1,378 (99.71%)",
    "727/1,428 (50.91%)",
    "1,340/1,378 (97.24%)",
    "648/1,428 (45.38%)",
  ],
  [
    "gemini-2.5-flash",
    "1,094/1,094 (100.00%)",
    "580/964 (60.17%)",
    "1,090/1,094 (99.63%)",
    "559/964 (57.99%)",
  ],
  [
    "gpt-4.1-2025-04-14",
    "1,263/1,263 (100.00%)",
    "1,061/1,472 (72.08%)",
    "1,249/1,263 (98.89%)",
    "982/1,472 (66.71%)",
  ],
  [
    "meta-llama/Llama-3.3-70B-Instruct-Turbo",
    "2,187/2,189 (99.91%)",
    "1,532/2,342 (65.41%)",
    "2,186/2,189 (99.86%)",
    "1,458/2,342 (62.25%)",
  ],
];

function ResultsTable({
  caption,
  headers,
  rows,
}: {
  caption: string;
  headers: string[];
  rows: string[][];
}) {
  return (
    <div
      className="table-wrap threshold-table"
      role="region"
      aria-label={caption}
      tabIndex={0}
    >
      <table>
        <caption>{caption}</caption>
        <thead>
          <tr>
            {headers.map((h) => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r[0]}>
              <th scope="row">{r[0]}</th>
              {r.slice(1).map((c, i) => (
                <td key={i}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
const breakdownHeaders = [
  "Generated at 0.1% FPR target",
  "Edited at 0.1% FPR target",
  "Generated at 0.01% FPR target",
  "Edited at 0.01% FPR target",
];

export function StylomeLargeEditLensReport() {
  return (
    <details className="benchmark-report">
      <summary>
        <span className="benchmark-identity">
          <span className="metadata-line">
            Stylome 1.2 Large · Pangram Labs
          </span>
          <strong>EditLens</strong>
          <span>
            Human-written, AI-edited, and AI-generated text across three test
            sections.
          </span>
        </span>
        <span
          className="benchmark-summary-metrics"
          aria-label="Stylome 1.2 Large EditLens highlights"
        >
          <BenchmarkMetric
            label="False-positive rate · 0.01% FPR target"
            value="0%"
            context="0 of 6,062 human rows flagged"
          />
          <BenchmarkMetric
            label="AI-generated detected · 0.01% FPR target"
            value="99.00%"
            context="5,865 of 5,924 generated passages"
          />
        </span>
        <span className="benchmark-toggle">Full results</span>
      </summary>
      <div className="benchmark-body">
        <p className="benchmark-context">
          Stylome 1.2 Large cleared every human row across all three EditLens
          test sections and detected 99.00% of fully generated passages at the
          0.01% FPR target.
        </p>
        <p>
          <a
            href="https://github.com/pangramlabs/EditLens"
            target="_blank"
            rel="noreferrer"
          >
            EditLens
          </a>{" "}
          contains human-written, AI-edited, and AI-generated text. This
          evaluation covers 18,192 rows in its standard, Enron email, and Llama
          test sections after excluding 27 rows due to dataset contamination.
          The detection tables count any AI-touched decision, whether edited or
          generated. The separate three-class table measures exact class matches
          using the dataset’s original labels.
        </p>
        <ResultsTable
          caption="Stylome 1.2 Large on EditLens"
          headers={[
            "Calibration FPR target",
            "Human false positives",
            "Generated detected",
            "Edited detected",
            "All-AI recall",
          ]}
          rows={[
            [
              "0.1%",
              "0 / 6,062 (0%)",
              "5,918 / 5,924 (99.90%)",
              "3,900 / 6,206 (62.84%)",
              "9,818 / 12,130 (80.94%)",
            ],
            [
              "0.01% · service default",
              "0 / 6,062 (0%)",
              "5,865 / 5,924 (99.00%)",
              "3,647 / 6,206 (58.77%)",
              "9,512 / 12,130 (78.42%)",
            ],
          ]}
        />
        <h3>Three-class results</h3>
        <p>
          The September 29 run also recorded exact class attribution with the
          frozen full-embedding KNN and five-neighbor fallback. These are
          exact-class recalls, not the detection recalls in the tables above.
          Confidence tiers were not evaluated in this rerun.
        </p>
        <ResultsTable
          caption="EditLens exact-class recall"
          headers={["True class", "0.1% FPR target", "0.01% FPR target"]}
          rows={[
            ["Human written", "6,062 / 6,062 (100%)", "6,062 / 6,062 (100%)"],
            ["AI edited", "3,296 / 6,206 (53.11%)", "3,161 / 6,206 (50.93%)"],
            [
              "AI generated",
              "5,885 / 5,924 (99.34%)",
              "5,837 / 5,924 (98.53%)",
            ],
          ]}
        />
        <ResultsTable
          caption="Three-class confusion · 0.01% FPR target"
          headers={[
            "True class",
            "Called human",
            "Called edited",
            "Called generated",
          ]}
          rows={[
            ["Human written", "6,062", "0", "0"],
            ["AI edited", "2,559", "3,161", "486"],
            ["AI generated", "59", "28", "5,837"],
          ]}
        />
        <p>
          Most missed edits were classified as human. These results do not
          establish performance on unseen sources.
        </p>
        <h3>Results by test section, source, and producer</h3>
        <p>
          Every section had zero human false positives at both operating points:
          0 of 2,035 standard-test human rows, 0 of 1,992 Enron rows, and 0 of
          2,035 Llama rows. Each detection cell gives the count, denominator,
          and recall.
        </p>
        <ResultsTable
          caption="EditLens by test section"
          headers={["Test section", ...breakdownHeaders]}
          rows={sections}
        />
        <ResultsTable
          caption="EditLens by source"
          headers={["Source", ...breakdownHeaders]}
          rows={sources}
        />
        <ResultsTable
          caption="EditLens by producer"
          headers={["Producer", ...breakdownHeaders]}
          rows={producers}
        />
        <h3>Evaluation details</h3>
        <p>
          The 6,062 human rows contain 4,022 distinct whitespace-normalized
          texts. None was flagged in any occurrence. Related human and AI texts
          appear across sections, so row counts are not counts of independent
          documents.
        </p>
        <p className="fine evaluation-note">
          Evaluated September 16–17, 2026, and rescored September 29, 2026. Both
          thresholds were fixed before scoring. All inputs were scored without
          truncation or length exclusion. The exact two-sided 95%
          false-positive-rate interval for the 4,022 unique human texts is
          0–0.0917%, under the independent-text assumption; shared authors and
          prompts limit that assumption.
        </p>
      </div>
    </details>
  );
}
